import { useEffect } from 'react';
import { App as NativeApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import {
  Capacitor,
  SystemBars,
  SystemBarsStyle,
  type PluginListenerHandle,
} from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Share } from '@capacitor/share';
import { StatusBar, Style } from '@capacitor/status-bar';

const AUTH_CALLBACK_URL = 'com.quranclub.app://auth/callback';

export interface QuranClubNativeBridge {
  readonly isNative: true;
  readonly platform: 'android' | 'ios';
  readonly authRedirectUrl: string;
  openExternalAuth(url: string): Promise<void>;
  share(options: { title?: string; text?: string; url?: string }): Promise<void>;
}

declare global {
  interface Window {
    __QC_NATIVE__?: QuranClubNativeBridge;
  }
}

function hasAuthResult(url: URL): boolean {
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  return Boolean(
    hash.get('access_token') ||
    hash.get('error') ||
    hash.get('error_description') ||
    url.searchParams.get('error') ||
    url.searchParams.get('error_description'),
  );
}

export function nativeAuthCallbackTarget(rawUrl: string, pathname = window.location.pathname): string | null {
  let callback: URL;
  try {
    callback = new URL(rawUrl);
  } catch {
    return null;
  }

  if (!hasAuthResult(callback)) return null;
  return `${pathname}${callback.search}${callback.hash}`;
}

/** Moves an OAuth callback from the native URL scheme onto the bundled app URL. */
export function applyNativeAuthCallback(rawUrl: string): boolean {
  const localCallback = nativeAuthCallbackTarget(rawUrl);
  if (!localCallback) return false;
  window.history.replaceState(null, '', localCallback);
  void Browser.close().catch(() => undefined);
  window.location.reload();
  return true;
}

function configureNativeRuntime(): () => void {
  if (!Capacitor.isNativePlatform()) return () => undefined;

  const platform = Capacitor.getPlatform();
  if (platform !== 'android' && platform !== 'ios') return () => undefined;

  document.documentElement.classList.add('capacitor-native');
  window.__QC_NATIVE__ = {
    isNative: true,
    platform,
    authRedirectUrl: AUTH_CALLBACK_URL,
    openExternalAuth: (url) => Browser.open({
      url,
      toolbarColor: '#0E2A20',
      presentationStyle: 'fullscreen',
    }),
    share: async (options) => {
      await Share.share({ ...options, dialogTitle: options.title || 'Share from Quran Club' });
    },
  };

  void SystemBars.show().catch(() => undefined);
  void SystemBars.setStyle({ style: SystemBarsStyle.Light }).catch(() => undefined);
  // The legacy overlay/background controls still apply on iOS. Android 15+
  // is edge-to-edge and is handled by SystemBars plus the global safe-area CSS.
  if (platform === 'ios') {
    void StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined);
    void StatusBar.setStyle({ style: Style.Light }).catch(() => undefined);
    void StatusBar.setBackgroundColor({ color: '#FBF7EC' }).catch(() => undefined);
  }
  void Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => undefined);

  const listeners: Array<Promise<PluginListenerHandle>> = [
    NativeApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) window.history.back();
      else void NativeApp.minimizeApp();
    }),
    NativeApp.addListener('appUrlOpen', ({ url }) => {
      applyNativeAuthCallback(url);
    }),
  ];

  void NativeApp.getLaunchUrl().then((launch) => {
    if (launch?.url) applyNativeAuthCallback(launch.url);
  });

  return () => {
    document.documentElement.classList.remove('capacitor-native');
    delete window.__QC_NATIVE__;
    void Promise.all(listeners).then((handles) => Promise.all(handles.map((handle) => handle.remove())));
  };
}

/** Sets up native behaviour before the legacy Quran reader runtime starts. */
export function useNativeRuntime(): boolean {
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => configureNativeRuntime(), []);

  return isNative;
}
